import React, { useState } from 'react';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import { FilterOptionsState, Popper, styled } from '@mui/material';
import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';
import { useAddProject } from 'routes/Admin/Budget/ProjectManagement/hooks/useAddProject';
import { useIsAdminCategories } from 'hooks/user';
import { Project } from 'routes/BudgetModule/shared/useGetProjects';
import { useTranslation } from 'react-i18next';
import { PopperProps } from '@mui/material/Popper/BasePopper.types';

const StyForm = styled('form', {
    name: 'StyForm',
})({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    padding: '1rem',
});

export type NewProjectPropositionT = { inputValue: string; name: string };

function isProject(
    arg: string | Project | NewProjectPropositionT,
): arg is Project {
    if (typeof arg === 'string') return false;
    if (!arg.name) return false;
    if (!('inscriptionsCount' in arg)) return false;
    return (
        typeof arg.name === 'string' &&
        typeof arg.inscriptionsCount === 'number'
    );
}

function isNewProjectPropositionT(
    arg: string | NewProjectPropositionT | Project | null,
): arg is NewProjectPropositionT {
    return (
        arg !== null &&
        typeof arg === 'object' &&
        'inputValue' in arg &&
        typeof arg.inputValue === 'string' &&
        'name' in arg &&
        typeof arg.name === 'string'
    );
}

/**
 * TODO: fix me, there is a positionning issue that appeared after React 18 & AgGrid 29->31 upgrades. Not sure of the culprit,
 * maybe another dependency. To reproduce, check this ticket: https://app.shortcut.com/mantic/story/25304/suite-%C3%A0-la-maj-aggrid-probl%C3%A8me-sur-la-modale-catoches-de-d-r
 * The temp fix is to prevent Popper.js from placing the modal on top of the form.
 * I've spent 2 hours on trying to fix the core issue; using disablePortal seems to be a deadend, changing anchorRef too. We should probably put loggers in the popper.js source
 * files at the exact place where it does its calculations.
 */
const CustomPopper = (props: PopperProps) => {
    return (
        <Popper
            {...props}
            placement="bottom"
            modifiers={[
                {
                    name: 'flip',
                    enabled: true,
                    options: {
                        fallbackPlacements: ['left-start', 'right-start'],
                    },
                },
            ]}
        />
    );
};

type PropsProjectMenuT = {
    anchorEl: (EventTarget & HTMLButtonElement) | null;
    closeProjectForm: () => void;
    unassignedProjects: Project[];
    addProjectToInscription: (projectId: number) => void;
};

const ProjectsSelectionMenu = ({
    anchorEl,
    closeProjectForm,
    unassignedProjects,
    addProjectToInscription,
}: PropsProjectMenuT) => {
    const { t } = useTranslation();
    const [newProject, setNewProject] = useState<null | {
        id?: number;
        name: string;
    }>(null);

    const { mutate: addProject } = useAddProject();
    const isAdminCategories = useIsAdminCategories();

    const filter = createFilterOptions();

    /**
     * When user enters a name that does not match any existing project,
     * suggests the creation of a new project with a label such as "Add {your input}".
     */
    const addNewProjectPropositionOption = (
        options: Project[],
        params: FilterOptionsState<string>,
    ) => {
        const filtered = filter(options, params);

        if (params.inputValue !== '') {
            const newProjectProposition: NewProjectPropositionT = {
                inputValue: params.inputValue,
                name: t('budgetProposal.addProject', {
                    project: params.inputValue,
                }),
            };
            filtered.push(newProjectProposition);
        }

        return filtered;
    };

    /**
     * User (with proper role) fills newProject value with new value.
     */
    const handleSelectAdminOptions = (
        newValue: string | Project | null | NewProjectPropositionT,
    ) => {
        // Case admin user just presses "enter" and creates a new project
        if (typeof newValue === 'string') {
            setNewProject({
                name: newValue,
            });
        }
        // Case admin user selects the "add newProject" proposition
        if (isNewProjectPropositionT(newValue)) {
            setNewProject({
                name: newValue.inputValue,
            });
        }
    };

    /**
     * User picks a project within the autocomplete list (any user) or pushes his own creation (admin Categories only)
     */
    const handleProjectSelection = (
        event: React.SyntheticEvent,
        newValue: string | Project | null | NewProjectPropositionT,
    ) => {
        if (isAdminCategories) {
            handleSelectAdminOptions(newValue);
        }

        if (newValue === null || isProject(newValue)) {
            setNewProject(newValue);
        }
    };

    /**
     * Create a new project. Then create his relationship with the inscription.
     */
    const createThenAssignNewProject = () => {
        addProject(
            { newProjectName: newProject!.name },
            {
                onSuccess: (responseData) => {
                    addProjectToInscription(responseData.content.data.id);
                },
            },
        );
        trackEventBudget(TRACKABLE_EVENTS.budget.general.addCategory);
    };

    /**
     * Handles project submit. If the given project does not exit, create it, then assign it to the inscription.
     * Else, just modify his inscription assignation.
     */
    const handleSubmitProject = (event: React.SyntheticEvent) => {
        event.preventDefault();
        closeProjectForm();

        if (newProject?.id) {
            addProjectToInscription(newProject.id);
        } else {
            createThenAssignNewProject();
        }
        setNewProject(null);
    };

    return (
        <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={closeProjectForm}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
            }}
        >
            <StyForm onSubmit={handleSubmitProject}>
                <Autocomplete
                    sx={{
                        marginBottom: '1rem',
                        width: '15rem',
                    }}
                    size="small"
                    options={unassignedProjects}
                    renderInput={(params) => (
                        <TextField
                            variant="standard"
                            {...params}
                            placeholder={t('budgetProposal.projectPlaceholder')}
                            label={t('budgetProposal.addAProject')}
                        />
                    )}
                    value={newProject}
                    onChange={handleProjectSelection}
                    filterOptions={
                        isAdminCategories
                            ? addNewProjectPropositionOption
                            : undefined
                    }
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                    getOptionLabel={(option: Project) => option.name}
                    renderOption={(props, option: Project) => (
                        <li {...props}>{option.name}</li>
                    )}
                    PopperComponent={CustomPopper}
                    freeSolo={isAdminCategories}
                    noOptionsText={t('budgetProposal.categoryDoesNotExist')}
                />
                <Button type="submit" disabled={!newProject}>
                    {t('common.add')}
                </Button>
            </StyForm>
        </Popover>
    );
};

export default ProjectsSelectionMenu;
